import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getManagementDashboardFilterOptions } from "../../../../../infrastructure/api/managementDashboard";
import {
  createDefaultFilters,
  filtersToSearchParams,
  isApiSuccess,
  normalizeFilterOptions,
  parseSearchParamsToFilters,
} from "./managementDashboardUtils";

const DashboardFilterBar = ({ loading, onBootstrapReady }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const bootstrapNotifiedRef = useRef(false);
  const [filters, setFilters] = useState(() =>
    parseSearchParamsToFilters(searchParams)
  );
  const [options, setOptions] = useState({
    provinces: [],
    cities: [],
    projectTypes: [],
    requestStatuses: [],
    lendingPartners: [],
    contractors: [],
    registrationChannels: [],
  });
  const [optionsLoading, setOptionsLoading] = useState(true);

  const loadOptions = useCallback(async (provinceId) => {
    try {
      setOptionsLoading(true);
      const res = await getManagementDashboardFilterOptions(
        provinceId ? Number(provinceId) : undefined
      );
      if (isApiSuccess(res) && (res.data ?? res.Data)) {
        setOptions(normalizeFilterOptions(res.data ?? res.Data));
      }
    } catch {
      // widget-level errors handled by parent KPI load
    } finally {
      setOptionsLoading(false);
      if (!bootstrapNotifiedRef.current) {
        bootstrapNotifiedRef.current = true;
        onBootstrapReady?.();
      }
    }
  }, [onBootstrapReady]);

  useEffect(() => {
    loadOptions(filters.provinceId);
  }, [filters.provinceId, loadOptions]);

  const updateField = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "provinceId") next.cityId = "";
      return next;
    });
  };

  const handleApply = () => {
    setSearchParams(filtersToSearchParams(filters), { replace: true });
  };

  const handleClear = () => {
    const defaults = createDefaultFilters();
    setFilters(defaults);
    setSearchParams(filtersToSearchParams(defaults), { replace: true });
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "16px",
        bgcolor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <Typography fontWeight={700} fontSize={15} sx={{ mb: 1.5 }}>
        فیلترهای داشبورد
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="از تاریخ"
            value={filters.dateFrom}
            onChange={(e) => updateField("dateFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="تا تاریخ"
            value={filters.dateTo}
            onChange={(e) => updateField("dateTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>استان</InputLabel>
            <Select
              label="استان"
              value={filters.provinceId}
              onChange={(e) => updateField("provinceId", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              {options.provinces.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small" disabled={!filters.provinceId}>
            <InputLabel>شهر</InputLabel>
            <Select
              label="شهر"
              value={filters.cityId}
              onChange={(e) => updateField("cityId", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              {options.cities.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>نوع نیروگاه</InputLabel>
            <Select
              label="نوع نیروگاه"
              value={filters.projectType}
              onChange={(e) => updateField("projectType", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              {options.projectTypes.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <TextField
            fullWidth
            size="small"
            label="حداقل kW"
            value={filters.minimumCapacityKw}
            onChange={(e) => updateField("minimumCapacityKw", e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <TextField
            fullWidth
            size="small"
            label="حداکثر kW"
            value={filters.maximumCapacityKw}
            onChange={(e) => updateField("maximumCapacityKw", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>نیاز به تأمین مالی</InputLabel>
            <Select
              label="نیاز به تأمین مالی"
              value={filters.needsLoan}
              onChange={(e) => updateField("needsLoan", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="1">بله</MenuItem>
              <MenuItem value="0">خیر</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>تأمین‌کننده مالی</InputLabel>
            <Select
              label="تأمین‌کننده مالی"
              value={filters.lendingPartnerCode}
              onChange={(e) => updateField("lendingPartnerCode", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              {options.lendingPartners.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>پیمانکار</InputLabel>
            <Select
              label="پیمانکار"
              value={filters.contractorInfoId}
              onChange={(e) => updateField("contractorInfoId", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              {options.contractors.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>وضعیت درخواست</InputLabel>
            <Select
              label="وضعیت درخواست"
              value={filters.requestStatus}
              onChange={(e) => updateField("requestStatus", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              {options.requestStatuses.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>کانال ثبت‌نام</InputLabel>
            <Select
              label="کانال ثبت‌نام"
              value={filters.registrationChannel}
              onChange={(e) => updateField("registrationChannel", e.target.value)}
            >
              <MenuItem value="">همه</MenuItem>
              {options.registrationChannels.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleApply} disabled={loading}>
              اعمال فیلتر
            </Button>
            <Button variant="outlined" onClick={handleClear} disabled={loading}>
              پاک‌سازی
            </Button>
            {optionsLoading ? (
              <Typography fontSize={12} color="text.secondary" sx={{ alignSelf: "center" }}>
                در حال بارگذاری گزینه‌ها...
              </Typography>
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardFilterBar;
